import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Markov chain poem trainer+generator in 29 sloc of Haskell",
  "description": "",
  "published": "2012-09-21T00:00:00.000Z",
  "redirect_from": ["/blog/markov-chain-poem-trainergenerator-in-29-sloc-of-haskell/swizec/5310"],
  "categories": "Amanda Palmer, Arts, Haskell, Markov chain, Randomness, Uncategorized",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`Another step in my `}<a parentName="p" {...{
        "href": "https://github.com/Swizec/Le-Thesis"
      }}>{`Automagic Poetry Generation`}</a>{` project.`}</p>
    <blockquote>
      <p parentName="blockquote">{`when i think we have tickets we't my abortion i drank it't my best friend melissa mahoney oh we tried to the building`}</p>
      <p parentName="blockquote">{`~The Markov Chain, based on Amanda Palmer's Oasis`}</p>
    </blockquote>
    <p>{`I'm making an evolutionary algorithm to generate poetry, but it needs a good base to start from. Random data is preferred, but being completely random means you waste the first couple of epochs just getting to something solid to work from - that's a waste of time.`}</p>
    <p>{`To make everything a bit smoother, I've written a `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Markov_chain",
        "title": "Markov chain"
      }}>{`markov chain`}</a>{` generator in `}<a parentName="p" {...{
        "href": "http://haskell.org",
        "title": "Haskell (programming language)"
      }}>{`Haskell`}</a>{` that generates the initial population. The principle is very simple:`}</p>
    <ol>
      <li parentName="ol">{`Read seed poem from file`}</li>
      <li parentName="ol">{`Tokenize`}</li>
      <li parentName="ol">{`Train markov chain`}</li>
      <li parentName="ol">{`A generator spits out an infinitely long list based on the markov chain`}</li>
      <li parentName="ol">{`Take X tokens`}</li>
      <li parentName="ol">{`Detokenize`}</li>
      <li parentName="ol">{`Done`}</li>
    </ol>
    <p>{`Simple.`}</p>
    <p>{`A quick brush up for people who don't breathe "hardcore" `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Computer_science",
        "title": "Computer science"
      }}>{`Comp Sci`}</a>{` stuff: A markov chain is essentially a probabilistic state automata, usually represented with a probability matrix. You take the current state and translate it into the next state by observing the proper probabilities.`}</p>
    <p>{`The output is a random string that looks a lot like some drunk tweets you might see on a friday night.`}</p>
    <h2 {...{
      "id": "le-code"
    }}>{`Le Code`}</h2>
    <p>{`Our main function is a simple convolution of a bunch of things:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-haskell"
      }}>{`start_population::(RandomGen g) => g -> String -> IO String
start_population gen start = do
  return . (foldr detokenize "") . (take Config.seed_length) . (produce gen start). chain . tokenize =<< readFile Config.seed_data
`}</code></pre>
    <p>{`Reading it from right to left you can see that it first reads some data, tokenizes it, makes the chain, produces some output, cuts it to the proper length and then shoves it back into a normal string.`}</p>
    <p>{`Simple.`}</p>
    <p>{`Because I wanted to handle punctuation and new lines, which are important in poems, I had to write my own tokenization and detokenization functions. Otherwise the built in `}<em parentName="p">{`words`}</em>{` function would be sufficient.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-haskell"
      }}>{`tokenize::String -> [String]
tokenize s = Prelude.filter (\\x -> x /= " " && x /= "") $
             Split.split (whenElt
                          (\\x -> isSeparator x || isPunctuation x || x == '\\n')) $
             Prelude.map toLower s

detokenize::String -> String -> String
detokenize a b
  | punctuation a || punctuation b = a++b
  | otherwise = a++" "++b
  where punctuation = (\\x -> length x > 0 && isPunctuation (x!!0))
`}</code></pre>
    <p>{`You can see that tokenize splits on pretty much everything and detokenize takes special care not to put spaces around punctuation.`}</p>
    <p>{`Another important step is building the chain itself.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-haskell"
      }}>{`chain::[String] -> Map String [String]
chain [now, last] =
  insert now [last] $ singleton last []
chain (token:xs) =
  insertWith (\\new old -> new++old) token [xs!!0] $ chain xs
`}</code></pre>
    <p>{`Simply put - this function builds a HashMap from a token to many tokens. The idea here is to make a note of every token that comes `}<em parentName="p">{`after`}</em>{` some other token. To make things simpler, if a pair of tokens happens twice, it will be recorded twice.`}</p>
    <p>{`This magically gives us the ability to properly weigh the random function that chooses what to generate next.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-haskell"
      }}>{`next_token::(RandomGen g) => g -> Map String [String] -> String -> (g, String)
next_token gen map s =
  let choices = findWithDefault [] s map
      (i, gen') = randomR (0, length choices - 1) gen
  in (gen', choices!!i)

produce::(RandomGen g) => g -> String -> Map String [String] -> [String]
produce gen s map =
  let (gen', next) = next_token gen map s
  in s:(produce gen' next map)
`}</code></pre>
    <p>{`I have a nasty suspicion the `}<em parentName="p">{`next_token`}</em>{` and `}<em parentName="p">{`produce`}</em>{` functions could be merged, but I found this easier to reason about.`}</p>
    <p><em parentName="p">{`Next_token`}</em>{` is the meat of our algorithm - it does nothing but take a token, find a list of its possible successors in the HashMap and return a random member of that list. To avoid any issues it will return an empty string if nothing is found.`}</p>
    <p>{`The `}<em parentName="p">{`produce`}</em>{` function takes care of driving `}<em parentName="p">{`next_token`}</em>{` and makes sure it gets a fresh random generator every time.`}</p>
    <p>{`A problem with my technique is that once you give a random generator to the markov chain, you're not getting it back. While it does ensure the result will always be fresh, you might be using a stale generator in other parts of your code if you're not careful.`}</p>
    <p>{`Maybe I should finally look into that random monad I've been hearing about.`}</p>
    <p>{`Either way, here's the full code, which is 38 sloc because I like including the function headers - makes code easier to think about, but I've tried and it does work without any type hints. Haskell is smarter than I am. Plus I added the whole part that only exposes `}<em parentName="p">{`start_population`}</em>{` to the outside world, which isn't otherwise necessary.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-haskell"
      }}>{`module Initiators.MarkovChain (
  start_population
  ) where

import System.Random
import Data.HashMap
import Data.List.Split as Split
import Data.Char

import Config

-- read corpus data
-- build markov chain
-- spit out data
start_population::(RandomGen g) => g -> String -> IO String
start_population gen start = do
  return . (foldr detokenize "") . (take Config.seed_length) . (produce gen start). chain . tokenize =<< readFile Config.seed_data tokenize::String -> [String]
tokenize s = Prelude.filter (\\x -> x /= " " && x /= "") $
             Split.split (whenElt
                          (\\x -> isSeparator x || isPunctuation x || x == '\\n')) $
             Prelude.map toLower s

detokenize::String -> String -> String
detokenize a b
  | punctuation a || punctuation b = a++b
  | otherwise = a++" "++b
  where punctuation = (\\x -> length x > 0 && isPunctuation (x!!0))

chain::[String] -> Map String [String]
chain [now, last] =
  insert now [last] $ singleton last []
chain (token:xs) =
  insertWith (\\new old -> new++old) token [xs!!0] $ chain xs

next_token::(RandomGen g) => g -> Map String [String] -> String -> (g, String)
next_token gen map s =
  let choices = findWithDefault [] s map
      (i, gen') = randomR (0, length choices - 1) gen
  in (gen', choices!!i)

produce::(RandomGen g) => g -> String -> Map String [String] -> [String]
produce gen s map =
  let (gen', next) = next_token gen map s
  in s:(produce gen' next map)
`}</code></pre>
    <h6 {...{
      "id": "related-articles"
    }}>{`Related articles`}</h6>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://isnerd.wordpress.com/2012/08/28/the-markovian-mind/"
        }}><img parentName="a" {...{
            "src": "https://i.zemanta.com/109018143_80_80.jpg",
            "alt": null
          }}></img></a><a parentName="li" {...{
          "href": "http://isnerd.wordpress.com/2012/08/28/the-markovian-mind/"
        }}>{`The Markovian Mind`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://terse-words.blogspot.com/2012/02/generating-gibberish-in-python-markov.html"
        }}><img parentName="a" {...{
            "src": "https://i.zemanta.com/noimg_79_80_80.jpg",
            "alt": null
          }}></img></a><a parentName="li" {...{
          "href": "http://terse-words.blogspot.com/2012/02/generating-gibberish-in-python-markov.html"
        }}>{`Generating Gibberish in Python - Markov Chains`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://swizec.com/blog/amdahls-law-in-action-27s-to-0-03s-by-changing-a-function/swizec/5106"
        }}><img parentName="a" {...{
            "src": "https://i.zemanta.com/107290574_80_80.jpg",
            "alt": null
          }}></img></a><a parentName="li" {...{
          "href": "http://swizec.com/blog/amdahls-law-in-action-27s-to-0-03s-by-changing-a-function/swizec/5106"
        }}>{`Amdahl's law in action - 27s to 0.03s by changing a function`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://swizec.com/blog/monads-monads-monads-monads/swizec/5206"
        }}><img parentName="a" {...{
            "src": "https://i.zemanta.com/110047375_80_80.jpg",
            "alt": null
          }}></img></a><a parentName="li" {...{
          "href": "http://swizec.com/blog/monads-monads-monads-monads/swizec/5206"
        }}>{`Monads, monads, monads, monads`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://swizec.com/blog/an-elegant-way-to-randomly-change-every-list-member-in-haskell/swizec/5060"
        }}><img parentName="a" {...{
            "src": "https://i.zemanta.com/106367996_80_80.jpg",
            "alt": null
          }}></img></a><a parentName="li" {...{
          "href": "http://swizec.com/blog/an-elegant-way-to-randomly-change-every-list-member-in-haskell/swizec/5060"
        }}>{`An elegant way to randomly change every list member in Haskell`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://blog.ezyang.com/2012/09/common-misconceptions-about-safe-haskell/"
        }}><img parentName="a" {...{
            "src": "https://i.zemanta.com/noimg_61_80_80.jpg",
            "alt": null
          }}></img></a><a parentName="li" {...{
          "href": "http://blog.ezyang.com/2012/09/common-misconceptions-about-safe-haskell/"
        }}>{`Common misconceptions about Safe Haskell`}</a></li>
    </ul>
    <p><a parentName="p" {...{
        "href": "http://www.zemanta.com/?px",
        "title": "Enhanced by Zemanta"
      }}><img parentName="a" {...{
          "src": "https://img.zemanta.com/zemified_e.png?x-id=3a0ab92f-5b51-431b-8e1b-7e2fee5375a0",
          "alt": "Enhanced by Zemanta"
        }}></img></a></p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      